import { Directive, DirectiveBinding, Ref, ref } from 'vue'

const getNodeProperties = (
  el: HTMLElement
): { key: string; node: HTMLElement; checkInputs: NodeListOf<HTMLInputElement> } => {
  const node = el.querySelector('input:not([type="checkbox"]):not([type="radio"]), input:checked, textarea') || el
  const checkInputs = el.querySelectorAll('input[type="checkbox"], input[type="radio"]') as NodeListOf<HTMLInputElement>
  return {
    key: camelize(el.id ? el.id : (node as HTMLElement).id),
    node: node as HTMLElement,
    checkInputs: checkInputs
  }
}

const RULES: Record<string, RuleFunction> = {}
export const defineRule = (name: string, validator: RuleFunction) => (RULES[name] = validator)
export type RuleFunction = (value: any, params: string[]) => boolean | string

export const camelize = (str: string): string =>
  str
    .split(/[- ]/)
    .map((item, index) => (index ? item.charAt(0).toUpperCase() + item.slice(1).toLowerCase() : item.toLowerCase()))
    .join('')

/**
 * Examples:
 *
 * Normale tag with comma separated rules:
 *      <mytag v-validate="'required,email'">
 *
 * Tag with rule object:
 *      <mytag v-validate="{required: true, email: true}">

 * Normale tag with comma separated rules and namespace:
 *      <mytag v-validate="'required,email'" data-validate-namespace="extraform">
 *
 *  Namespace can be any string containing letters and digits.
 */
const validateDirective: Directive = {
  mounted: (el: HTMLElement, binding: DirectiveBinding) => {
    if (!binding.value) return

    const validator = (binding.instance as any).validator as ValidatorType

    if (!validator) throw new Error(`Validator-Object missing for component "${binding.instance?.$options.name}"`)

    const { key, node: $inputNode, checkInputs: $checkInputs } = getNodeProperties(el)
    let domId = $inputNode.id
    if (!domId) {
      $inputNode.id = domId = Math.floor(Math.random() * 0xca0e373ebffff + 0x05c5e45240000).toString(36)
    }
    const validatorId = el.dataset.validateNamespace ? `${el.dataset.validateNamespace}:${key}` : key
    const error = validator[validatorId] ? ref(validator[validatorId].error) : ref('')

    const doValidate = () => {
      let rules = binding.value instanceof Function ? binding.value() : binding.value

      const ruleList: Record<string, any> = {}

      if (typeof rules === 'string') {
        rules = rules.split('|')
      }

      if (Array.isArray(rules)) {
        rules.forEach(ruleString => {
          const [key, paramStr] = ruleString.split(':')
          ruleList[key] = paramStr?.split(',') || true
        })
      } else Object.entries(rules).forEach(([key, value]) => (ruleList[key] = value))

      if (document.getElementById(domId) === null) {
        delete validator[validatorId]
        return true
      }
      const { node: $inputNode } = getNodeProperties(el)
      const value = ($inputNode as HTMLInputElement)?.value || ''

      const errors: string[] = []

      Object.entries(ruleList).forEach(([rule, params]) => {
        if (!(rule in RULES)) return

        const res = RULES[rule](value, params)
        if (true === res) return

        errors.push(res?.toString())
      })

      error.value = errors.length ? errors[0] : ''
      ;(el.querySelector('.form__element') || el).classList.toggle('is--invalid', !!errors.length)
      return !errors.length
    }

    validator[validatorId] = {
      error: error,
      validate: doValidate
    }
    const inputType = ($inputNode as HTMLInputElement)?.type

    if (inputType === 'text' || $inputNode.nodeName.toLowerCase() === 'textarea') {
      $inputNode.addEventListener('blur', doValidate)
      return
    }

    $checkInputs.forEach($input => {
      $input.addEventListener('change', doValidate)
    })

    if (!$checkInputs.length) {
      $inputNode.addEventListener('change', doValidate)
    }
  }
}

export const validateComponent = (validator: ValidatorType, namespace = ''): boolean => {
  let result = true
  // Make sure the only fields with the given namespace are validated.
  //  Namespace key is: "space:myfield"
  //        without is: "myfield"
  for (const id of Object.keys(validator).filter(
    k => (namespace === '' && !k.includes(':')) || k.indexOf(namespace + ':') === 0
  )) {
    if (!validator[id].validate()) result = false
  }

  return result
}

export default validateDirective
export type ValidatorType = Record<string, { error: Ref<string>; validate: () => boolean }>
